// First-touch campaign attribution. On the first landing that carries UTM params or a referrer,
// we snapshot them to localStorage and never overwrite — so the campaign that originally brought
// the visitor in survives later internal navigations and is available to send at signup. Field
// names intentionally match AuthRegisterRequest so the stored object spreads straight into the
// register payload.

const STORAGE_KEY = 'otb_attribution';

export type Attribution = {
	utmSource?: string;
	utmMedium?: string;
	utmCampaign?: string;
	utmContent?: string;
	utmTerm?: string;
	referrerUrl?: string;
};

function readParam(params: URLSearchParams, key: string): string | undefined {
	return params.get(key) ?? undefined;
}

/**
 * Capture attribution on first landing. No-op if we've already stored something (first-touch),
 * if there's nothing to capture, or if storage is unavailable (private mode / native).
 */
export function captureAttribution(): void {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		if (localStorage.getItem(STORAGE_KEY)) {
			return;
		}

		const params = new URLSearchParams(window.location.search);
		const attribution: Attribution = {
			utmSource: readParam(params, 'utm_source'),
			utmMedium: readParam(params, 'utm_medium'),
			utmCampaign: readParam(params, 'utm_campaign'),
			utmContent: readParam(params, 'utm_content'),
			utmTerm: readParam(params, 'utm_term'),
			referrerUrl: document.referrer || undefined,
		};

		const hasSignal = Object.values(attribution).some(Boolean);
		if (hasSignal) {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
		}
	} catch {
		// localStorage can throw in private mode or when disabled — attribution is best-effort.
	}
}

export function getStoredAttribution(): Attribution {
	if (typeof window === 'undefined') {
		return {};
	}

	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		return raw ? (JSON.parse(raw) as Attribution) : {};
	} catch {
		return {};
	}
}
